import { Box, FormHelperText, styled, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { BareAxios, hideGrecaptcha, mountGrecaptcha } from "../common/utils";
import { baseApi } from "../common/constants";
import poweredbycolor from "./images/Powered-By-Color-Logo.jpg";
import { SignInCard, SignInContainer } from "./subComps/loginPieces";
import { H5 } from "./account/components/Typography";
import { Context } from "../common/context";
import type { Size, AppContext, ContextValue } from "../types";

const BottomImage = styled(`img`)({
    width: `100px`,
});

const BottomImageBox = styled(Box)({
    marginTop: `30px`,
    paddingBottom: 40,
    textAlign: `center`,
});

const GetCode = ({ size, context }: { size: Size; context: AppContext }) => {
    const [code, setCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [bc] = useState(new BroadcastChannel(`li_login`));
    const [searchParams] = useSearchParams();
    const [loaded, setLoaded] = useState(false);
    const [showPage, setShowPage] = useState(false);

    const [expiredTimeout, setExpiredTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const data = searchParams.get(`data`);
    const isGc = searchParams.get(`gc`);
    const navigate = useNavigate();

    useEffect(() => {
        BareAxios.post(`${baseApi}getcode`, { data })
            .then((c: { data: { code: string; ts: string; sid: string; newUser?: boolean; gift?: string } }) => {
                setCode(c.data.code);
                const timeLeft = 1000 * 15 * 60 - (Date.now() - new Date(c.data.ts).getTime());
                setExpiredTimeout(
                    setTimeout(
                        () =>
                            navigate(c.data.gift ? `/getmygift/login` : c.data.newUser ? `/signup` : `/login`, {
                                state: { reason: `expired` },
                            }),
                        timeLeft
                    )
                );

                if (!isGc) {
                    localStorage.setItem(`sid`, c.data.sid);
                    try {
                        bc.postMessage(`code page loaded`);
                    } catch {
                        (() => {})();
                    }
                }
                setLoaded(true);
            })
            .catch((e: { response: { data?: { reason?: string; newUser?: boolean; gift?: string } } }) => {
                if (e.response.data?.reason === `expired`) {
                    if (expiredTimeout) clearTimeout(expiredTimeout);
                    navigate(
                        e.response.data.gift
                            ? `/getmygift/login?sp=${e.response.data.gift}`
                            : e.response.data.newUser
                              ? `/signup`
                              : `/login`,
                        { state: { reason: `expired` } }
                    );
                }
                setError(`Sorry, something went wrong. Please try again.`);
                setLoaded(true);
            });

        bc.onmessage = (m: MessageEvent) => {
            if (!isGc) {
                if (m.data === `new user`) {
                    if (expiredTimeout) clearTimeout(expiredTimeout);
                    navigate(`/register`);
                }
                if (m.data === `old user`) {
                    if (expiredTimeout) clearTimeout(expiredTimeout);
                    navigate(`/`);
                }
            }
        };

        mountGrecaptcha();
        return () => {
            hideGrecaptcha();
            try {
                bc.close();
            } catch {
                (() => {})();
            }
        };
    }, []);

    useEffect(() => {
        if (loaded) {
            setTimeout(() => {
                setShowPage(true);
            }, 500);
        }
    }, [loaded]);

    return showPage ? (
        <Context.Consumer>
            {(value: ContextValue) =>
                !value.context?.adminConfig ? null : (
                    <SignInContainer size={size} direction="column" justifyContent="space-between">
                        <Box
                            sx={{
                                margin: `0 auto ${size === `small` ? 0 : `24px`}`,
                            }}
                        >
                            <Box
                                sx={{ height: `40px` }}
                                component="img"
                                src={context?.adminConfig?.mall?.get_my_gift_logo}
                            />
                        </Box>
                        <SignInCard size={size} variant="outlined">
                            {value.context?.adminConfig?.mall?.enable_login_page && (
                                <>
                                    <Typography
                                        component="h1"
                                        variant="h4"
                                        sx={{
                                            fontFamily: `Plus Jakarta Sans`,
                                            width: `100%`,
                                            fontSize: size === `small` ? `28px` : `36px`,
                                            letterSpacing: `0.01em`,
                                            fontWeight: 700,
                                            textAlign: `center`,
                                        }}
                                    >
                                        Verification Code
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: `flex`,
                                            flexDirection: `column`,
                                            width: `100%`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                textAlign: `center`,
                                                fontSize: `16px`,
                                                marginBottom: `24px`,
                                            }}
                                        >
                                            {isGc && `Enter this code to validate your email.`}
                                            {!isGc &&
                                                `We weren't able to instantly log you in. No worries, you can use this
                                            code to log in.`}
                                        </Typography>
                                        <H5
                                            id="li-code"
                                            sx={{
                                                textAlign: `center`,
                                                fontSize: `40px`,
                                                margin: `20px 0`,
                                                letterSpacing: `0.3em`,
                                                color: `#0fa654`,
                                                background: `#0fa6540d`,
                                            }}
                                        >
                                            {code}
                                        </H5>

                                        {error && (
                                            <FormHelperText
                                                error
                                                sx={{
                                                    mt: 1,
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    minHeight: `22px`,
                                                    minWidth: `2px`,
                                                    textAlign: `center`,
                                                }}
                                            >
                                                {error}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                </>
                            )}
                        </SignInCard>
                        {sessionStorage.getItem(`sp`) ? (
                            <BottomImageBox>
                                <BottomImage src={poweredbycolor} />
                            </BottomImageBox>
                        ) : null}
                    </SignInContainer>
                )
            }
        </Context.Consumer>
    ) : null;
};

export default GetCode;
